import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCookie } from "../utils/cookies";
import { useLoading } from "../context/LoadingContext";
import TransitionOverlay from "../components/TransitionOverlay";

const RegisterVerify = () => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showTransition, setShowTransition] = useState(false);

  const email = localStorage.getItem("unverifiedEmail");
  const verificationType = localStorage.getItem("verificationType");

  // Code validation
  const codeLen = code.length === 6;
  const codeDigits = /^\d+$/.test(code);
  const codeTouched = code.length > 0;

  useEffect(() => {
    const email = localStorage.getItem("unverifiedEmail");
    const type = localStorage.getItem("verificationType");
    if (!email || !type) { navigate("/login"); return; }
    fetch("https://awaretherapycenter.ro/accounts/csrf-verification/", { credentials: "include" });
    requestAnimationFrame(() => setTimeout(() => setLoaded(true), 50));
  }, [navigate]);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handleBack = () => navigate("/login", { replace: true });
    window.addEventListener("popstate", handleBack);
    return () => window.removeEventListener("popstate", handleBack);
  }, [navigate]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleTransitionComplete = () => {
    window.location.href = "/";
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    startLoading();
    try {
      const response = await fetch("https://awaretherapycenter.ro/accounts/send-code/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRFToken": getCookie("csrftoken") },
        credentials: "include",
        body: new URLSearchParams({ email }),
      });
      if (response.ok) {
        setSuccess("Codul a fost retrimis!"); setError("");
        setResendCooldown(60);
      } else {
        const data = await response.json();
        setError(data.error || "Nu s-a putut retrimite codul."); setSuccess("");
      }
    } catch (err) { setError("Eroare de rețea."); setSuccess(""); }
    finally { stopLoading(); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codeLen || !codeDigits) { setError("Codul trebuie să conțină exact 6 cifre."); return; }
    startLoading();
    try {
      const response = await fetch("https://awaretherapycenter.ro/accounts/verify-code/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRFToken": getCookie("csrftoken") },
        credentials: "include",
        body: new URLSearchParams({ email, code }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess("Cont verificat cu succes!"); setError("");
        stopLoading();

        if (verificationType === "register") {
          // Auto-login: store tokens + user info
          if (data.access && data.refresh) {
            localStorage.setItem("access_token", data.access);
            localStorage.setItem("refresh_token", data.refresh);
          }
          if (data.user) {
            localStorage.setItem("user_first_name", data.user.first_name);
            localStorage.setItem("user_last_name", data.user.last_name);
          }
          localStorage.removeItem("unverifiedEmail");
          localStorage.removeItem("verificationType");
          setShowTransition(true);
        } else if (verificationType === "reset") {
          navigate("/new-password");
        }
      } else { setError(data.error || "Cod invalid sau expirat."); setSuccess(""); }
    } catch (err) { setError("Eroare de rețea."); setSuccess(""); }
    finally { stopLoading(); }
  };

  const CheckIcon = () => (
    <svg viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l3 3 5-5"/></svg>
  );

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Inter:wght@300;400;500&display=swap');

    .rv-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(250,247,242,0.92); backdrop-filter: blur(30px); -webkit-backdrop-filter: blur(30px); }
    .rv-nav-inner { max-width: 1400px; margin: 0 auto; padding: 28px 48px; display: flex; align-items: center; justify-content: space-between; }
    .rv-nav-logo { font-family: 'Cormorant', Georgia, serif; font-size: 22px; font-weight: 400; letter-spacing: 5px; text-transform: uppercase; color: #074F4F; text-decoration: none; }
    .rv-nav-links { display: flex; gap: 44px; list-style: none; padding: 0; margin: 0; }
    .rv-nav-links a { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: #2d2d2d; text-decoration: none; transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); position: relative; padding-bottom: 4px; }
    .rv-nav-links a::after { content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 1px; background: #074F4F; transform: scaleX(0); transform-origin: right; transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
    .rv-nav-links a:hover::after { transform: scaleX(1); transform-origin: left; }
    .rv-nav-links a:hover { color: #074F4F; }

    .rv-grain { position: fixed; inset: 0; z-index: 10; pointer-events: none; opacity: 0.02; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); background-size: 128px; }

    .rv-scene { min-height: 100vh; padding-top: 100px; padding-bottom: 60px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; background: #FAF7F2; }
    .rv-scene::before { content: ''; position: absolute; top: -20%; right: -10%; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(218,235,219,0.25) 0%, transparent 70%); pointer-events: none; }
    .rv-scene::after { content: ''; position: absolute; bottom: -15%; left: -8%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(245,225,208,0.2) 0%, transparent 70%); pointer-events: none; }

    .rv-content { position: relative; z-index: 2; max-width: 480px; width: 100%; padding: 0 24px; text-align: center; opacity: 0; transform: translateY(40px); transition: all 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s; }
    .rv-scene.loaded .rv-content { opacity: 1; transform: translateY(0); }

    .rv-icon { width: 64px; height: 64px; border-radius: 50%; border: 1.5px solid #C6A769; display: flex; align-items: center; justify-content: center; margin: 0 auto 32px; color: #C6A769; }

    .rv-heading { font-family: 'Cormorant', Georgia, serif; font-size: 36px; font-weight: 300; color: #074F4F; letter-spacing: -0.02em; margin-bottom: 12px; }
    .rv-sub { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 300; color: #8a8a8a; line-height: 1.6; margin-bottom: 36px; }

    .rv-card { background: #fff; padding: 40px 36px; text-align: left; box-shadow: 0 1px 3px rgba(7,79,79,0.02), 0 8px 32px rgba(7,79,79,0.04), 0 24px 60px rgba(7,79,79,0.03); position: relative; }
    .rv-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: #C6A769; transform: scaleX(0); transform-origin: left; transition: transform 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.6s; }
    .rv-scene.loaded .rv-card::before { transform: scaleX(1); }

    .rv-label { display: block; font-family: 'Inter', sans-serif; font-size: 9px; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; color: #074F4F; margin-bottom: 10px; }
    .rv-input { width: 100%; height: 56px; padding: 0 18px; font-family: 'Inter', sans-serif; font-size: 22px; font-weight: 300; color: #2d2d2d; background: #FAF7F2; border: 1px solid transparent; border-radius: 0; outline: none; transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); -webkit-appearance: none; letter-spacing: 8px; text-align: center; }
    .rv-input:focus { background: #fff; border-color: #C6A769; box-shadow: 0 0 0 3px rgba(198,167,105,0.06); }
    .rv-input::placeholder { color: #b5b5b5; font-weight: 300; letter-spacing: 8px; font-size: 22px; }

    .rv-checks { display: flex; flex-direction: column; gap: 5px; margin-top: 10px; }
    .rv-check { display: flex; align-items: center; gap: 8px; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 400; color: #b5b5b5; transition: color 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .rv-check.valid { color: #5a9a6a; }
    .rv-check-icon { width: 15px; height: 15px; border-radius: 50%; border: 1.5px solid #d4d4d4; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .rv-check.valid .rv-check-icon { border-color: #5a9a6a; background: #5a9a6a; }
    .rv-check-icon svg { width: 9px; height: 9px; opacity: 0; transform: scale(0.5); transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .rv-check.valid .rv-check-icon svg { opacity: 1; transform: scale(1); }

    .rv-error { font-family: 'Inter', sans-serif; font-size: 12px; color: #b33; background: rgba(187,51,51,0.04); border-left: 2px solid #C6A769; padding: 12px 16px; margin-top: 16px; line-height: 1.5; }
    .rv-success { font-family: 'Inter', sans-serif; font-size: 12px; color: #5a9a6a; background: rgba(90,154,106,0.06); border-left: 2px solid #5a9a6a; padding: 12px 16px; margin-top: 16px; line-height: 1.5; }

    .rv-submit { width: 100%; height: 52px; margin-top: 24px; font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 3.5px; text-transform: uppercase; color: #074F4F; background: #C6A769; border: none; cursor: pointer; position: relative; overflow: hidden; transition: transform 0.2s; }
    .rv-submit::before { content: ''; position: absolute; inset: 0; background: #fff; transform: translateY(101%); transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
    .rv-submit:hover::before { transform: translateY(0); }
    .rv-submit span { position: relative; z-index: 1; }
    .rv-submit:active { transform: scale(0.98); }

    .rv-resend { text-align: center; margin-top: 20px; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 300; color: #8a8a8a; }
    .rv-resend-link { color: #C6A769; font-weight: 500; cursor: pointer; background: none; border: none; font-family: 'Inter', sans-serif; font-size: 12px; padding: 0; position: relative; padding-bottom: 1px; }
    .rv-resend-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 1px; background: #C6A769; transform: scaleX(0); transform-origin: right; transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
    .rv-resend-link:hover::after { transform: scaleX(1); transform-origin: left; }
    .rv-resend-link:disabled { color: #b5b5b5; cursor: default; }
    .rv-resend-link:disabled::after { display: none; }

    .rv-footer { background: #074F4F; padding: 80px 48px 32px; }
    .rv-f-inner { max-width: 1200px; margin: 0 auto; }
    .rv-f-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 56px; }
    .rv-f-brand-name { font-family: 'Cormorant', Georgia, serif; font-size: 22px; font-weight: 400; letter-spacing: 5px; text-transform: uppercase; color: #FAF7F2 !important; margin-bottom: 14px; }
    .rv-footer p, .rv-f-brand-desc { font-family: 'Inter', sans-serif !important; font-size: 13px !important; line-height: 1.7 !important; color: rgba(250,247,242,0.4) !important; max-width: 260px; }
    .rv-f-social { display: flex; gap: 16px; margin-top: 24px; }
    .rv-f-social a { width: 36px; height: 36px; border: 1px solid rgba(198,167,105,0.25); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Inter', sans-serif; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: rgba(250,247,242,0.5) !important; text-decoration: none; transition: all 0.3s; }
    .rv-f-social a:hover { background: #C6A769; border-color: #C6A769; color: #074F4F !important; }
    .rv-f-col h5 { font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: #C6A769 !important; margin-bottom: 20px; }
    .rv-f-col ul { list-style: none; padding: 0; margin: 0; }
    .rv-f-col li { margin-bottom: 12px; }
    .rv-f-col a { font-family: 'Inter', sans-serif; font-size: 13px; color: rgba(250,247,242,0.4) !important; text-decoration: none; transition: color 0.3s; }
    .rv-f-col a:hover { color: #C6A769 !important; }
    .rv-f-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(198,167,105,0.2), transparent); margin-bottom: 32px; }
    .rv-f-bottom { display: flex; justify-content: space-between; font-family: 'Inter', sans-serif; font-size: 11px; color: rgba(250,247,242,0.25) !important; }
    .rv-f-bottom a { color: rgba(250,247,242,0.25) !important; margin-left: 20px; text-decoration: none; transition: color 0.3s; }
    .rv-f-bottom a:hover { color: #C6A769 !important; }

    @media (max-width: 1024px) { .rv-nav-links { display: none; } .rv-f-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 600px) { .rv-card { padding: 32px 24px; } .rv-heading { font-size: 28px; } .rv-nav-inner { padding: 20px 24px; } .rv-footer { padding: 60px 24px 24px; } .rv-f-grid { grid-template-columns: 1fr; } }
  `;

  return (
    <div>
      <style>{css}</style>

      <TransitionOverlay
        active={showTransition}
        onComplete={handleTransitionComplete}
      />

      <nav className="rv-nav">
        <div className="rv-nav-inner">
          <a href="/" className="rv-nav-logo">Aware</a>
          <ul className="rv-nav-links">
            <li><a href="/">Acasă</a></li>
            <li><a href="/despre-noi">Despre noi</a></li>
            <li><a href="/servicii">Servicii</a></li>
            <li><a href="/evenimente">Evenimente</a></li>
            <li><a href="/#contact">Contact</a></li>
          </ul>
        </div>
      </nav>

      <div className={"rv-scene" + (loaded ? " loaded" : "")}>
        <div className="rv-grain" />
        <div className="rv-content">
          <div className="rv-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h1 className="rv-heading">Confirmă codul</h1>
          <p className="rv-sub">Introdu codul de verificare trimis pe email pentru a continua</p>

          <div className="rv-card">
            <form onSubmit={handleSubmit}>
              <label className="rv-label">Cod de verificare</label>
              <input
                className="rv-input"
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(""); }}
                placeholder="000000"
                maxLength={6}
                required
              />

              {codeTouched && (
                <div className="rv-checks">
                  <div className={"rv-check" + (codeDigits ? " valid" : "")}>
                    <span className="rv-check-icon"><CheckIcon /></span>
                    Doar cifre
                  </div>
                  <div className={"rv-check" + (codeLen ? " valid" : "")}>
                    <span className="rv-check-icon"><CheckIcon /></span>
                    6 cifre
                  </div>
                </div>
              )}

              {error && <div className="rv-error">{error}</div>}
              {success && <div className="rv-success">{success}</div>}

              <button type="submit" className="rv-submit"><span>Verifică</span></button>
            </form>

            <div className="rv-resend">
              Nu ai primit codul?{' '}
              <button className="rv-resend-link" onClick={handleResend} disabled={resendCooldown > 0}>
                {resendCooldown > 0 ? `Retrimite în ${resendCooldown}s` : 'Retrimite codul'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer className="rv-footer">
        <div className="rv-f-inner">
          <div className="rv-f-grid">
            <div>
              <div className="rv-f-brand-name">Aware</div>
              <p>Un spațiu sigur pentru conștientizare, echilibru și sprijin emoțional.</p>
              <div className="rv-f-social"><a href="#">Ig</a><a href="#">Fb</a><a href="#">Tk</a></div>
            </div>
            <div className="rv-f-col"><h5>Servicii</h5><ul><li><a href="#">Terapie Individuală</a></li><li><a href="#">Terapie de Cuplu</a></li><li><a href="#">Familie &amp; Parenting</a></li><li><a href="#">Anxietate &amp; Depresie</a></li></ul></div>
            <div className="rv-f-col"><h5>Terapeuți</h5><ul><li><a href="#">Alexandra Strambeanu</a></li><li><a href="#">Luna Cosmari</a></li><li><a href="#">Bianca Istratescu</a></li><li><a href="#">Kathrin Moculity</a></li></ul></div>
            <div className="rv-f-col"><h5>Aware</h5><ul><li><a href="/despre-noi">Despre noi</a></li><li><a href="/#contact">Contact</a></li><li><a href="/evenimente">Evenimente</a></li><li><a href="/blog">Blog</a></li></ul></div>
          </div>
          <div className="rv-f-divider"></div>
          <div className="rv-f-bottom">
            <span>© 2025 Aware — Centru de Psihoterapie</span>
            <div><a href="/conditii">Termeni</a><a href="/confidentialitate">Confidențialitate</a></div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RegisterVerify;
